import * as React from "react";
import axios from "axios";
import clsx from "clsx";
import { useSettings } from "@Tmp/Hooks";
import { AddToCartProps } from ".";
import { CartWithWatch } from "@Tmp/Icons";
import { Input } from "@Tmp/Fields";

const AddToCart: React.FC<AddToCartProps> = ({
    product,
    qty: qtyProp = 1,
    showQtyField,
    iconOnly = false,
    defaultIcon = "fas fa-shopping-cart",
    overrideIcon,
    allowDiscontinued = false,
    fullWidth = false,
    ...props
}) => {
    const tmpSettings = useSettings();

    const [qtyErrorMessage, setQtyErrorMessage] = React.useState<string>();
    const [qty, setQty] = React.useState(qtyProp);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        setQty(qtyProp);
    }, [qtyProp]);

    const handleUpdateQty = (qtyVal: string | number) => {
        setQtyErrorMessage(undefined);
        if (qtyVal === "") {
            setQty(undefined);
        }
        qtyVal = parseInt(qtyVal as any);

        if (!qtyVal || qtyVal < 1) {
            return;
        }
        setQty(qtyVal);
    };

    const renderText = (styles) => {
        if (iconOnly) {
            return;
        }

        if (product.is_replaced && !allowDiscontinued) {
            return <span style={styles}>{props?.settings?.replaced_text ?? "Erstattet"}</span>;
        }

        if (product.is_discontinued && !allowDiscontinued) {
            return <span style={styles}>{props?.settings?.discontinued_text ?? "Udgået"}</span>;
        }

        if (product.stock.qty <= 0 && props.settings?.pre_order_text && product.pre_order) {
            return <span style={styles}>{props.settings?.pre_order_text}</span>;
        }

        if (product.stock.qty <= 0 && props.settings?.cart_no_inventory_text) {
            return <span style={styles}>{props.settings?.cart_no_inventory_text}</span>;
        }

        if (props?.settings?.cart_button_text) {
            return <span style={styles}>{props.settings?.cart_button_text}</span>;
        }
    };

    const renderIcon = () => {
        if (loading) {
            return <i className="fas fa-spinner fa-pulse" />;
        }

        if (overrideIcon) {
            return <i className={overrideIcon} />;
        }

        if (product.is_replaced && !allowDiscontinued) {
            return <i className="fas fa-sync-alt" />;
        }

        if (product.is_discontinued && !allowDiscontinued) {
            return <i className="fas fa-ban" />;
        }

        if (product.stock.qty <= 0) {
            return <CartWithWatch />;
        }

        return <i className={props?.settings?.cart_icon?.value ?? defaultIcon} />;
    };

    const renderButtonContent = (direction) => {
        let icon_spacing =
            props.settings?.cart_text_icon_spacing?.size +
            props.settings?.cart_text_icon_spacing?.unit;

        if (!icon_spacing) {
            icon_spacing = 10;
        }

        let styles: React.CSSProperties = {
            marginLeft: icon_spacing,
        };
        if (direction === "right") {
            styles = {
                marginRight: icon_spacing,
            };
            return (
                <React.Fragment>
                    {renderText(styles)}
                    {renderIcon()}
                </React.Fragment>
            );
        }
        return (
            <React.Fragment>
                {renderIcon()}
                {renderText(styles)}
            </React.Fragment>
        );
    };

    const action = () => {
        if (!Number.isInteger(qty) || qty < 1) {
            setQtyErrorMessage("Ugyldig værdi");
            return;
        }

        if (props.action) {
            props.action(product, qty, setLoading);
            return;
        }

        setLoading(true);

        axios
            .post(
                "/wp-json/tmp/v1/cart/item/" + product.id,
                {
                    qty: qty,
                },
                {
                    headers: { "Content-type": "application/json" },
                }
            )
            .then((response) => {
                setLoading(false);
                if (response.status === 200) {
                    const success = "Tilføjet " + qty + " af " + product.sku + " til kurven";

                    tmp.cart.load();
                    tmp.cart.notice(success);
                } else {
                    tmp.notice("Noget gik galt");
                }
            });
    };

    const disabled = product ? product.is_discontinued && !allowDiscontinued : true;
    showQtyField =
        showQtyField == undefined ? props.settings?.add_to_cart_enable_qty === "yes" : showQtyField;

    if (
        product.cart_banned ||
        tmpSettings.is_shop_disabled ||
        (product.is_shop_disabled && !product.is_discontinued)
    ) {
        return null;
    }

    return (
        <div
            className={clsx(
                "tmp-add-to-cart__container",
                {
                    "tmp-add-to-cart__full-width": fullWidth,
                },
                props?.classes?.container
            )}
        >
            {showQtyField && (
                <Input
                    value={qty ?? ""}
                    onChange={(e) => {
                        handleUpdateQty(e.target.value);
                    }}
                    input={{
                        className: "tmp-add-to-cart__amount-input",
                        type: "number",
                        step: "1",
                        min: "1",
                        max: "999",
                    }}
                    iconBefore="fa fa-minus"
                    iconBeforeContainer={{
                        className: "cursor-pointer",
                        onClick: () => {
                            handleUpdateQty((qty ?? 2) - 1);
                        },
                    }}
                    iconAfter="fa fa-plus"
                    iconAfterContainer={{
                        className: "cursor-pointer",
                        onClick: () => {
                            handleUpdateQty((qty ?? 0) + 1);
                        },
                    }}
                    errorMessage={qtyErrorMessage}
                    wrapper={{
                        className: "tmp-add-to-cart__amount-container",
                    }}
                />
            )}
            <button
                className={clsx(
                    "tmp-add-to-cart__button",
                    "tmp-btn tmp-btn-sm tmp-add-to-cart-btn",
                    {
                        "tmp-inventory-status-discontinued":
                            product.is_discontinued && !allowDiscontinued,
                    },
                    props?.classes?.button
                )}
                value={product ? product.id : ""}
                disabled={loading ? true : disabled}
                onClick={action}
            >
                {renderButtonContent(props?.settings?.cart_icon_alignment)}
            </button>
        </div>
    );
};

export default AddToCart;
